import { forwardRef, useId } from "react";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { Fieldset, type FieldsetProps } from "../Fieldset";
import { Radio, type RadioProps } from "../Radio";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

import * as S from "./RadioGroup.styled";

type RadioGroupInput = Omit<RadioProps, "alertProps" | "name" | "onChange">;

export type RadioGroupProps = PropsWithOptionalTestID &
  Pick<
    FieldsetProps,
    "legend" | "legendHint" | "legendTooltipProps" | "legendHintTooltipProps"
  > & {
    alertProps?:
      | InputAlertsProps["alerts"][number]
      | InputAlertsProps["alerts"];
    name: string;
    inputs: RadioGroupInput[];
    onChange?: RadioProps["onChange"];
  };

const RadioGroup = forwardRef<HTMLFieldSetElement, RadioGroupProps>(
  (props, ref) => {
    const {
      alertProps,
      legend,
      legendHint,
      legendTooltipProps,
      legendHintTooltipProps,
      name,
      inputs,
      onChange,
      testID = "radio-group",
    } = props;

    const { tokens } = useDesignTokens();
    const { radioGroup } = tokens;

    const alertId = useId();
    const ariaAlertAssociationProps = buildAlertAssociationProps(
      alertId,
      props
    );

    return (
      <InputWrapper
        gap={radioGroup.gap}
        alert={
          alertProps && (
            <div id={alertId} data-testid={`${testID}-alerts`}>
              <InputAlerts
                alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
              />
            </div>
          )
        }
      >
        <Fieldset
          ref={ref}
          legend={legend}
          legendHint={legendHint}
          legendTooltipProps={legendTooltipProps}
          legendHintTooltipProps={legendHintTooltipProps}
          testID={testID}
          ariaAlertAssociationProps={ariaAlertAssociationProps}
        >
          <S.List>
            {inputs.map((input, index) => (
              <S.ListItem key={index}>
                <Radio {...input} name={name} onChange={onChange} />
              </S.ListItem>
            ))}
          </S.List>
        </Fieldset>
      </InputWrapper>
    );
  }
);

RadioGroup.displayName = "RadioGroup";

export default RadioGroup;